/**
 * Compact system-style row for a single workflow audit event, rendered
 * interleaved with comments in the Flow Designer's Comments tab (see
 * `FlowCommentsPanel`). Mirrors `CommentsTab`'s comment-row layout (icon
 * gutter, header line, body) so the merged timeline reads as one list.
 *
 * @module FlowDesigner/components/WorkflowEventRow
 */
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Flex, Text, TagBadge } from '@chatsdet/ui';
import { ArrowRight, ArrowSquareOut, FlowArrow, Robot, Ticket } from '@phosphor-icons/react';
import type { WorkflowEvent } from '../hooks/useWorkflowEvents';
import { workflowEventRevisionHref } from './workflowEventRevision';

const formatDateTime = (value: string) => {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatRelativeTime = (value: string) => {
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return value;
  }
};

const MANAGEMENT_STATUS_LABEL: Record<string, string> = {
  active: 'active',
  paused: 'paused',
  blocked: 'blocked',
  under_construction: 'under construction',
  deprecated: 'deprecated',
};

function statusLabel(status: string | null): string {
  if (!status) return '—';
  return MANAGEMENT_STATUS_LABEL[status] ?? status;
}

function eventText(event: WorkflowEvent): string {
  if (event.eventType === 'status_changed') {
    return `Status changed: ${statusLabel(event.fromStatus)} → ${statusLabel(event.toStatus)}`;
  }
  if (event.eventType === 'definition_changed') {
    return 'Workflow definition changed';
  }
  if (event.eventType === 'maintenance_completed') {
    return event.reason ?? 'Maintenance agent updated the workflow';
  }
  if (event.eventType === 'maintenance_gave_up') {
    return event.reason ?? 'Maintenance agent could not complete this run';
  }
  if (event.eventType === 'maintenance_feedback_answered') {
    return `Answered the maintenance agent: ${event.reason ?? ''}`;
  }
  if (event.eventType === 'cache_edit_reconciled') {
    return event.reason
      ? `Cache reconciled after edits — ${event.reason}`
      : 'Cache reconciled after edits';
  }
  if (event.eventType === 'cache_pinned') {
    return event.reason ? `Cache pinned — ${event.reason}` : 'Cache pinned';
  }
  if (event.eventType === 'cache_unpinned') {
    return event.reason ? `Cache unpinned — ${event.reason}` : 'Cache unpinned';
  }
  if (event.eventType === 'node_deactivated') {
    return `Node deactivated — "${event.nodeLabel ?? 'a step'}"`;
  }
  if (event.eventType === 'node_reactivated') {
    return `Node reactivated — "${event.nodeLabel ?? 'a step'}"`;
  }
  // Unknown event type: render a neutral fallback rather than assuming any
  // specific event shape.
  return (event.eventType as string).replace(/_/g, ' ');
}

const MAINTENANCE_EVENT_TYPES: ReadonlySet<WorkflowEvent['eventType']> = new Set([
  'maintenance_completed',
  'maintenance_gave_up',
  'maintenance_feedback_answered',
]);

// Event types whose `reason` is folded into `eventText` above, so the row
// must not also render `event.reason` as a separate line below.
const EVENT_TYPES_WITH_REASON_IN_TEXT: ReadonlySet<WorkflowEvent['eventType']> = new Set([
  ...MAINTENANCE_EVENT_TYPES,
  'cache_edit_reconciled',
  'cache_pinned',
  'cache_unpinned',
]);

export type WorkflowEventActor = {
  displayName: string | null;
  primaryEmail: string | null;
} | null;

interface WorkflowEventRowProps {
  event: WorkflowEvent;
  /** Resolved org member for `event.actorId`, or null for agent/system/unknown actors. */
  actor: WorkflowEventActor;
  /** When provided and the event is node-anchored, clicking the row focuses the node. */
  onNodeClick?: (nodeId: string) => void;
}

// The server enforces an http(s)-only allowlist on `externalUrl` at write time;
// this pattern is client-side defense-in-depth against a stored `javascript:`/
// `data:` URL ever reaching an `href`.
const SAFE_EXTERNAL_URL = /^https?:\/\//i;

/** Ticket chip: internal issue links into the Issues page; external opens the tracker URL. */
function TicketChip({ ticket }: { ticket: WorkflowEvent['tickets'][number] }) {
  const href = ticket.issueId
    ? `/issues?issueId=${ticket.issueId}`
    : (ticket.externalUrl ?? undefined);
  if (!href) return null;
  const isSafeLink = Boolean(ticket.issueId) || SAFE_EXTERNAL_URL.test(href);
  let label: string;
  if (ticket.issueId) {
    label = 'Issue';
  } else if (ticket.externalIdentifier) {
    label = ticket.externalIdentifier;
  } else {
    try {
      label = new URL(ticket.externalUrl ?? '').hostname;
    } catch {
      label = 'Link';
    }
  }
  const badge = (
    <TagBadge name={label} color="gray" size="sm" icon={<Ticket size={11} weight="bold" />} />
  );
  if (!isSafeLink) return badge;
  return (
    <a href={href} target={ticket.issueId ? undefined : '_blank'} rel="noopener noreferrer">
      {badge}
    </a>
  );
}

export function WorkflowEventRow({ event, actor, onNodeClick }: WorkflowEventRowProps) {
  const isAgentOrSystem = event.actorKind !== 'user';
  const actorName = isAgentOrSystem
    ? event.actorId
    : (actor?.displayName ?? actor?.primaryEmail ?? 'Unknown user');

  const canFocusNode = Boolean(event.nodeId && onNodeClick);
  const revisionNumber = event.metadata?.revisionNumber;
  const revisionHref = workflowEventRevisionHref(event);

  return (
    <div className="flex gap-3 flex-1 min-w-0">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {event.eventType === 'status_changed' ? (
          <ArrowRight size={14} weight="bold" />
        ) : MAINTENANCE_EVENT_TYPES.has(event.eventType) ? (
          <Robot size={14} weight="bold" />
        ) : (
          <FlowArrow size={14} weight="bold" />
        )}
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <Flex align="baseline" gap="xs" wrap className="min-w-0">
          <Text size="sm" weight="semibold" className="truncate inline-flex items-center gap-1">
            {isAgentOrSystem && <Robot size={12} weight="bold" className="text-muted-foreground" />}
            {actorName}
          </Text>
          <Text
            size="xs"
            className="text-muted-foreground/70 shrink-0"
            title={formatDateTime(event.createdAt)}
          >
            · {formatRelativeTime(event.createdAt)}
          </Text>
        </Flex>
        <div className="text-sm text-foreground/90">
          {canFocusNode ? (
            <button
              type="button"
              onClick={() => onNodeClick?.(event.nodeId as string)}
              className="text-left hover:underline"
            >
              {eventText(event)}
            </button>
          ) : (
            <Text size="sm">{eventText(event)}</Text>
          )}
        </div>
        {revisionHref && (
          <a
            href={revisionHref}
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Open immutable revision ${revisionNumber}`}
          >
            Revision {revisionNumber}
            <ArrowSquareOut size={11} aria-hidden="true" />
          </a>
        )}
        {event.reason && !EVENT_TYPES_WITH_REASON_IN_TEXT.has(event.eventType) && (
          <Text size="sm" className="text-muted-foreground whitespace-pre-wrap">
            {event.reason}
          </Text>
        )}
        {event.tickets.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-0.5">
            {event.tickets.map((ticket) => (
              <TicketChip key={ticket.id} ticket={ticket} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
